<script setup>
// 🧾 MAĞAZA SATIŞI — kağıt "SİPARİŞ FORMU" koçanının dijital hali.
// Ürün grubu modeli: ürün kodu bir kez girilir, renkler çiplerden ÇOKLU seçilir,
// her renk kendi adet satırını açar (kağıt formdaki model→renkler düzeni).
// Katalogda olmayan ürün elle girilerek devam edilebilir. Fiş WhatsApp'a RESİM
// olarak gider. Mobil öncelikli tasarım.
import { ref, computed } from 'vue';
import { useRoute } from 'vue-router';
import {
  fetchMarketplaceJson,
  postMarketplaceJsonBody,
  postMarketplaceJson,
  patchMarketplaceJson,
  deleteMarketplaceJson,
} from 'dashboard/helpers/marketplaceApi';

const route = useRoute();
const accountId = route.params.accountId;

const tab = ref('yeni'); // yeni | defter | musteriler | rapor
const trToday = () =>
  new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Istanbul' });

// ---- FORM DURUMU ----
const SELLERS = ['Semra Koçaklı', 'Neriman Koçaklı'];
const sellerName = ref(SELLERS[0]);
const source = ref('fabrika');
const currency = ref('USD');
const invoiceType = ref('yok');
const deliveryDate = ref('');
const note = ref('');
// Satış tarihi: geçmişe kayıt atılabilir (dün yapılan satış düne işlenir).
const soldOn = ref(trToday());
// Düzenleme modu: Defter'den "Düzenle" ile dolan fiş; kaydedince PATCH gider.
const editingSale = ref(null);

// ---- MÜŞTERİ KARTI ----
// Kayıtlı müşteri isimle aranıp seçilir ya da yeni kart açılır. Ülke + telefon
// + müşterinin çalıştığı ÖZEL ihracat kargo firması (yurtiçi taşıyıcı değil)
// karta işlenir — satışla birlikte backend'de kart bulunur/açılır/güncellenir.
const emptyCustomer = () => ({
  id: null,
  name: '',
  country: '',
  phone: '',
  cargo_company: '',
});
const customer = ref(emptyCustomer());
const custSuggestions = ref([]);
const custShow = ref(false);
const custSearching = ref(false);
let custTimer = null;
let custSeq = 0;
const onCustomerInput = () => {
  customer.value.id = null;
  clearTimeout(custTimer);
  const q = customer.value.name.trim();
  if (q.length < 2) {
    custSuggestions.value = [];
    custShow.value = false;
    custSearching.value = false;
    return;
  }
  custSearching.value = true;
  const seq = (custSeq += 1);
  custTimer = setTimeout(async () => {
    try {
      const data = await fetchMarketplaceJson(
        accountId,
        `store_customers?q=${encodeURIComponent(q)}`
      );
      if (seq !== custSeq) return;
      custSuggestions.value = data.customers || [];
      custShow.value = custSuggestions.value.length > 0;
    } catch {
      if (seq === custSeq) custSuggestions.value = [];
    } finally {
      if (seq === custSeq) custSearching.value = false;
    }
  }, 200);
};
const hideCustSuggestionsSoon = () => {
  setTimeout(() => {
    custShow.value = false;
  }, 200);
};
const pickCustomer = c => {
  customer.value = {
    id: c.id,
    name: c.name,
    country: c.country || '',
    phone: c.phone || '',
    cargo_company: c.cargo_company || '',
  };
  custSuggestions.value = [];
  custShow.value = false;
};
// İskonto/İlave: fiş para biriminde, KDV hariç ara toplama uygulanır.
// iskonto = aşağı yuvarlama (455$ → 5$ → 450$), ilave = yukarı fark (455$ → 460$).
// Backend'de tek 'discount' alanı taşır: pozitif iskonto, negatif ilave.
const discountAmount = ref(null);
const adjMode = ref('iskonto'); // iskonto | ilave

// Kargo: fiş para biriminde elle girilen nakliye bedeli (Neriman isteği).
// Mal bedelinden ayrı durur: yarım faturada yarıya inmez, KDV'ye girmez,
// genel toplama eklenir ve kâr hesabına karışmaz.
const shippingAmount = ref(null);
const shippingOpen = ref(false);
// Ödeme şekli: elden (nakit) | havale (EFT).
const paymentMethod = ref('elden');
const paymentOptions = {
  elden: { icon: '💵', label: 'Elden', hint: 'nakit teslim' },
  havale: { icon: '🏦', label: 'Havale', hint: 'banka / EFT' },
};

// Ürün grubu: kod + birim fiyat grup seviyesinde; renkler adet satırları olarak altında.
const emptyGroup = () => ({
  code: '',
  title: '',
  cost_usd: null,
  colors: [],
  unit_price: null,
  rows: [], // { color, qty, manual }
  suggestions: [],
  showSuggestions: false,
  notFound: false,
  searching: false,
  seq: 0,
});
const groups = ref([emptyGroup()]);
const saving = ref(false);
const error = ref('');
const createdSale = ref(null);
const previewOpen = ref(false);

const KDV = 0.1;
const symbols = { USD: '$', TRY: '₺', EUR: '€' };
const sym = computed(() => symbols[currency.value]);
const sourceLabels = {
  fabrika: 'Fabrikadan Satış',
  internet_depo: 'İnternet Mağaza Deposu',
};
const invoiceLabels = {
  yok: 'Fatura istenmedi',
  tam: 'Tam fatura',
  yarim: 'Yarım fatura talepli',
};

const validLines = computed(() =>
  groups.value.flatMap(g => {
    if (!g.code || !(g.unit_price > 0)) return [];
    return g.rows
      .filter(r => r.color && r.qty > 0)
      .map(r => ({
        code: g.code,
        color: r.color,
        qty: r.qty,
        unit_price: g.unit_price,
        cost_usd: g.cost_usd,
        title: g.title,
      }));
  })
);
const totalQty = computed(() =>
  validLines.value.reduce((s, l) => s + Number(l.qty), 0)
);
const grossSubtotal = computed(() =>
  validLines.value.reduce((s, l) => s + l.qty * l.unit_price, 0)
);
const discountVal = computed(() => {
  const raw = Math.max(Number(discountAmount.value) || 0, 0);
  if (adjMode.value === 'ilave') return -raw;
  return Math.min(raw, grossSubtotal.value);
});
const realSubtotal = computed(() => grossSubtotal.value - discountVal.value);
const fisDiscount = computed(() =>
  invoiceType.value === 'yarim' ? discountVal.value / 2 : discountVal.value
);
const fisGross = computed(() =>
  invoiceType.value === 'yarim' ? grossSubtotal.value / 2 : grossSubtotal.value
);
const fisSubtotal = computed(() =>
  invoiceType.value === 'yarim' ? realSubtotal.value / 2 : realSubtotal.value
);
const fisKdv = computed(() =>
  invoiceType.value === 'yok' ? 0 : fisSubtotal.value * KDV
);
const shippingVal = computed(() =>
  Math.max(Number(shippingAmount.value) || 0, 0)
);
const goodsTotal = computed(() => fisSubtotal.value + fisKdv.value);
const fisTotal = computed(() => goodsTotal.value + shippingVal.value);

const fmt = n =>
  Number(n || 0).toLocaleString('tr-TR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

const groupQty = g => g.rows.reduce((s, r) => s + (Number(r.qty) || 0), 0);
const groupTotal = g => groupQty(g) * (Number(g.unit_price) || 0);

// ---- ÜRÜN ARAMA (3+ hane, 150ms; bayat cevap koruması) ----
const timers = new WeakMap();
const onCodeInput = group => {
  clearTimeout(timers.get(group));
  group.notFound = false;
  group.title = '';
  const q = group.code.trim();
  if (q.length < 3) {
    group.suggestions = [];
    group.showSuggestions = false;
    group.searching = false;
    return;
  }
  group.searching = true;
  const seq = (group.seq += 1);
  timers.set(
    group,
    setTimeout(async () => {
      try {
        const data = await fetchMarketplaceJson(
          accountId,
          `store_sales/lookup?q=${encodeURIComponent(q)}`
        );
        if (seq !== group.seq) return; // bayat cevap
        group.suggestions = data.products || [];
        group.showSuggestions = group.suggestions.length > 0;
        group.notFound = group.suggestions.length === 0;
      } catch {
        if (seq === group.seq) group.suggestions = [];
      } finally {
        if (seq === group.seq) group.searching = false;
      }
    }, 150)
  );
};

const hideSuggestionsSoon = group => {
  setTimeout(() => {
    group.showSuggestions = false;
  }, 200);
};

const pickSuggestion = (group, p) => {
  group.code = p.model_code || p.parent_code;
  group.title = p.title;
  group.cost_usd = p.cost_usd;
  group.colors = p.colors || [];
  group.rows = [];
  group.notFound = false;
  if (!group.unit_price && p.wholesale_usd) group.unit_price = p.wholesale_usd;
  group.suggestions = [];
  group.showSuggestions = false;
};

const colorSelected = (group, c) => group.rows.some(r => r.color === c);
const toggleColor = (group, c) => {
  const idx = group.rows.findIndex(r => r.color === c);
  if (idx >= 0) group.rows.splice(idx, 1);
  else group.rows.push({ color: c, qty: null, manual: false });
};
const addManualRow = group =>
  group.rows.push({ color: '', qty: null, manual: true });
const removeRow = (group, row) => {
  group.rows = group.rows.filter(r => r !== row);
};

const addGroup = () => groups.value.push(emptyGroup());
const removeGroup = group => {
  groups.value = groups.value.filter(g => g !== group);
  if (!groups.value.length) groups.value.push(emptyGroup());
};

const belowCost = g =>
  g.cost_usd &&
  g.unit_price &&
  currency.value === 'USD' &&
  Number(g.unit_price) < Number(g.cost_usd);

// Maliyet zorunlu: katalog ürünü maliyetiyle gelir, katalog dışında elle
// girilir — girilmeden satış kaydedilemez (günlük kâr raporu buna dayanır).
const needsCost = g => !!g.code && !(Number(g.cost_usd) > 0);
const groupsMissingCost = computed(() =>
  groups.value.filter(
    g =>
      g.code &&
      g.unit_price > 0 &&
      g.rows.some(r => r.color && r.qty > 0) &&
      !(Number(g.cost_usd) > 0)
  )
);

// ---- OLUŞTURMA ----
const canPreview = computed(
  () =>
    customer.value.name.trim() &&
    validLines.value.length > 0 &&
    !groupsMissingCost.value.length
);

const submit = async (skipSend = false) => {
  saving.value = true;
  error.value = '';
  try {
    const body = {
      customer_name: customer.value.name.trim(),
      customer: { ...customer.value, name: customer.value.name.trim() },
      seller_name: sellerName.value,
      source: source.value,
      currency: currency.value,
      invoice_type: invoiceType.value,
      delivery_date: deliveryDate.value || null,
      sold_on: soldOn.value || null,
      note: note.value,
      discount: discountVal.value || 0,
      shipping_cost: shippingVal.value || 0,
      payment_method: paymentMethod.value,
      skip_send: skipSend || undefined,
      lines: validLines.value,
    };
    const wasEdit = !!editingSale.value;
    const data = wasEdit
      ? await patchMarketplaceJson(
          accountId,
          `store_sales/${editingSale.value.id}`,
          body
        )
      : await postMarketplaceJsonBody(accountId, 'store_sales', body);
    createdSale.value = { ...data.sale, was_edit: wasEdit };
    editingSale.value = null;
    previewOpen.value = false;
  } catch (e) {
    error.value = e.message;
  } finally {
    saving.value = false;
  }
};

const resetForm = () => {
  customer.value = emptyCustomer();
  invoiceType.value = 'yok';
  deliveryDate.value = '';
  note.value = '';
  discountAmount.value = null;
  adjMode.value = 'iskonto';
  shippingAmount.value = null;
  shippingOpen.value = false;
  paymentMethod.value = 'elden';
  soldOn.value = trToday();
  groups.value = [emptyGroup()];
  createdSale.value = null;
  editingSale.value = null;
};

// Defter'den fiş düzenleme: satış forma geri yüklenir, kaydedince aynı
// fiş no/token ile güncellenir (dış fiş linki değişmez).
const startEdit = sale => {
  editingSale.value = sale;
  customer.value = sale.customer
    ? {
        id: sale.customer.id,
        name: sale.customer.name,
        country: sale.customer.country || '',
        phone: sale.customer.phone || '',
        cargo_company: sale.customer.cargo_company || '',
      }
    : { ...emptyCustomer(), name: sale.customer_name };
  sellerName.value = sale.seller_name || SELLERS[0];
  source.value = sale.source;
  currency.value = sale.currency;
  invoiceType.value = sale.invoice_type;
  deliveryDate.value = sale.delivery_date || '';
  note.value = sale.note || '';
  adjMode.value = sale.discount < 0 ? 'ilave' : 'iskonto';
  discountAmount.value = sale.discount ? Math.abs(sale.discount) : null;
  shippingAmount.value = sale.shipping_cost ? Number(sale.shipping_cost) : null;
  shippingOpen.value = Number(sale.shipping_cost) > 0;
  paymentMethod.value = sale.payment_method || 'elden';
  soldOn.value = sale.sold_on || trToday();
  const map = new Map();
  (sale.lines || []).forEach(l => {
    const key = `${l.code}|${l.unit_price}`;
    if (!map.has(key)) {
      map.set(key, {
        ...emptyGroup(),
        code: l.code,
        title: l.title || '',
        cost_usd: l.cost_usd,
        unit_price: l.unit_price,
      });
    }
    map.get(key).rows.push({ color: l.color, qty: l.qty, manual: true });
  });
  groups.value = map.size ? [...map.values()] : [emptyGroup()];
  createdSale.value = null;
  tab.value = 'yeni';
  window.scrollTo(0, 0);
};

// ---- DEFTER ----
const sales = ref([]);
const defterLoading = ref(false);
const defterQ = ref('');
const defterSource = ref('');
const loadDefter = async () => {
  defterLoading.value = true;
  try {
    const params = new URLSearchParams();
    if (defterQ.value) params.set('q', defterQ.value);
    if (defterSource.value) params.set('source', defterSource.value);
    const data = await fetchMarketplaceJson(
      accountId,
      `store_sales?${params.toString()}`
    );
    sales.value = data.sales || [];
  } finally {
    defterLoading.value = false;
  }
};
const openDefter = () => {
  tab.value = 'defter';
  loadDefter();
};
const resendSale = async sale => {
  // eslint-disable-next-line no-alert
  if (
    !window.confirm(`${sale.fis_no} fişi tekrar WhatsApp ile gönderilsin mi?`)
  )
    return;
  await postMarketplaceJson(accountId, `store_sales/${sale.id}/resend`);
  loadDefter();
};
const cancelSale = async sale => {
  // eslint-disable-next-line no-alert
  if (
    !window.confirm(
      `${sale.fis_no} — ${sale.customer_name} satışı İPTAL edilsin mi?`
    )
  )
    return;
  await postMarketplaceJson(accountId, `store_sales/${sale.id}/cancel`);
  loadDefter();
};
const deleteSale = async sale => {
  // eslint-disable-next-line no-alert
  if (
    !window.confirm(
      `${sale.fis_no} — ${sale.customer_name} fişi TAMAMEN SİLİNSİN mi? Bu geri alınamaz.`
    )
  )
    return;
  await deleteMarketplaceJson(accountId, `store_sales/${sale.id}`);
  loadDefter();
};

// ---- MÜŞTERİLER (kart listesi + düzenleme) ----
const customersList = ref([]);
const customersLoading = ref(false);
const customersQ = ref('');
const savingCustomerId = ref(null);
const loadCustomers = async () => {
  customersLoading.value = true;
  try {
    const q = customersQ.value
      ? `?q=${encodeURIComponent(customersQ.value)}`
      : '';
    const data = await fetchMarketplaceJson(accountId, `store_customers${q}`);
    customersList.value = data.customers || [];
  } finally {
    customersLoading.value = false;
  }
};
const openMusteriler = () => {
  tab.value = 'musteriler';
  loadCustomers();
};
const saveCustomer = async c => {
  savingCustomerId.value = c.id;
  try {
    await patchMarketplaceJson(accountId, `store_customers/${c.id}`, {
      name: c.name,
      country: c.country,
      phone: c.phone,
      cargo_company: c.cargo_company,
      note: c.note,
    });
    c.saved = true;
    setTimeout(() => {
      c.saved = false;
    }, 1500);
  } finally {
    savingCustomerId.value = null;
  }
};

// ---- GÜNLÜK RAPOR (satış · maliyet · kâr) ----
const repFrom = ref(trToday());
const repTo = ref(trToday());
const report = ref(null);
const reportLoading = ref(false);
const loadReport = async () => {
  reportLoading.value = true;
  try {
    report.value = await fetchMarketplaceJson(
      accountId,
      `store_sales/report?from=${repFrom.value}&to=${repTo.value}`
    );
  } finally {
    reportLoading.value = false;
  }
};
const openRapor = () => {
  tab.value = 'rapor';
  loadReport();
};
const shiftDay = delta => {
  const d = new Date(`${repFrom.value}T12:00:00`);
  d.setDate(d.getDate() + delta);
  const s = d.toISOString().slice(0, 10);
  repFrom.value = s;
  repTo.value = s;
  loadReport();
};
const setToday = () => {
  repFrom.value = trToday();
  repTo.value = trToday();
  loadReport();
};
const setLast7 = () => {
  const d = new Date(`${trToday()}T12:00:00`);
  d.setDate(d.getDate() - 6);
  repFrom.value = d.toISOString().slice(0, 10);
  repTo.value = trToday();
  loadReport();
};
const setMonth = () => {
  const t = trToday();
  repFrom.value = `${t.slice(0, 8)}01`;
  repTo.value = t;
  loadReport();
};
// Raporu PDF olarak indir: sunucu üretir, dönen URL yeni sekmede açılır.
const pdfLoading = ref(false);
const downloadPdf = async () => {
  pdfLoading.value = true;
  try {
    const data = await fetchMarketplaceJson(
      accountId,
      `store_sales/report_pdf?from=${repFrom.value}&to=${repTo.value}`
    );
    if (data.url) window.open(data.url, '_blank');
  } finally {
    pdfLoading.value = false;
  }
};
// Sade Türkçe özeti sesli okur (erişilebilirlik: düşük görme).
const speakOzet = () => {
  if (!report.value?.ozet || !window.speechSynthesis) return;
  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(report.value.ozet);
  u.lang = 'tr-TR';
  u.rate = 0.95;
  window.speechSynthesis.speak(u);
};
</script>

<template>
  <div class="flex-1 min-w-0 overflow-auto bg-n-background">
    <div class="max-w-6xl mx-auto p-3 md:p-6">
      <!-- Başlık + sekmeler -->
      <div
        class="flex flex-wrap items-center justify-between gap-3 mb-4 md:mb-6"
      >
        <h1 class="text-xl md:text-2xl font-bold text-n-slate-12">
          🧾 Mağaza Satışı
        </h1>
        <div class="flex gap-2">
          <button
            class="px-4 py-2.5 rounded-lg text-base font-semibold"
            :class="
              tab === 'yeni'
                ? 'bg-woot-600 text-white'
                : 'bg-n-slate-3 text-n-slate-11'
            "
            @click="tab = 'yeni'"
          >
            Yeni Satış
          </button>
          <button
            class="px-4 py-2.5 rounded-lg text-base font-semibold"
            :class="
              tab === 'defter'
                ? 'bg-woot-600 text-white'
                : 'bg-n-slate-3 text-n-slate-11'
            "
            @click="openDefter"
          >
            📒 Defter
          </button>
          <button
            class="px-4 py-2.5 rounded-lg text-base font-semibold"
            :class="
              tab === 'musteriler'
                ? 'bg-woot-600 text-white'
                : 'bg-n-slate-3 text-n-slate-11'
            "
            @click="openMusteriler"
          >
            👤 Müşteriler
          </button>
          <button
            class="px-4 py-2.5 rounded-lg text-base font-semibold"
            :class="
              tab === 'rapor'
                ? 'bg-woot-600 text-white'
                : 'bg-n-slate-3 text-n-slate-11'
            "
            @click="openRapor"
          >
            📊 Rapor
          </button>
        </div>
      </div>

      <!-- ============ YENİ SATIŞ ============ -->
      <div v-if="tab === 'yeni'">
        <!-- Başarı ekranı -->
        <div
          v-if="createdSale"
          class="bg-green-50 border-2 border-green-500 rounded-xl p-6 md:p-8 text-center"
        >
          <div class="text-5xl mb-3">✅</div>
          <h2 class="text-2xl font-bold text-n-slate-12 mb-2">
            {{ createdSale.fis_no }}
            {{ createdSale.was_edit ? 'güncellendi' : 'oluşturuldu' }}
          </h2>
          <p class="text-lg text-n-slate-11 mb-1">
            {{ createdSale.customer_name }} —
            {{ fmt(createdSale.totals.fis_total) }}
            {{ createdSale.currency_symbol }}
            <template v-if="createdSale.totals.try_equivalent">
              (≈ {{ fmt(createdSale.totals.try_equivalent) }} ₺)
            </template>
          </p>
          <p class="text-base text-n-slate-11 mb-6">
            <template v-if="createdSale.sent_to?.length">
              📲 Fiş resmi gönderildi: {{ createdSale.sent_to.join(', ') }}
            </template>
            <template v-else>WhatsApp gönderilmedi.</template>
          </p>
          <div class="flex flex-wrap justify-center gap-3">
            <a
              :href="createdSale.fis_url"
              target="_blank"
              class="px-6 py-3 rounded-lg bg-woot-600 text-white text-lg font-semibold"
            >
              Fişi Aç
            </a>
            <button
              class="px-6 py-3 rounded-lg bg-n-slate-3 text-n-slate-12 text-lg font-semibold"
              @click="resetForm"
            >
              Yeni Satış
            </button>
          </div>
        </div>

        <template v-else>
          <!-- Düzenleme modu uyarısı -->
          <div
            v-if="editingSale"
            class="flex flex-wrap items-center justify-between gap-2 bg-yellow-50 border-2 border-yellow-500 rounded-xl px-4 py-3 mb-4"
          >
            <p class="text-base md:text-lg font-bold text-yellow-800">
              ✏️ {{ editingSale.fis_no }} düzenleniyor — kaydedince fiş aynı
              numarayla güncellenir.
            </p>
            <button
              class="px-4 py-2 rounded-lg bg-n-slate-3 text-n-slate-12 font-semibold"
              @click="resetForm"
            >
              Vazgeç
            </button>
          </div>

          <!-- Satış bilgileri + müşteri kartı -->
          <div
            class="bg-n-solid-1 rounded-xl border border-n-weak p-4 md:p-5 mb-4"
          >
            <div
              class="grid grid-cols-1 md:grid-cols-[2fr_1fr_1.2fr_1.6fr] gap-3 md:gap-4 mb-3"
            >
              <label class="block relative">
                <span class="text-sm font-semibold text-n-slate-11">
                  Müşteri * <template v-if="customer.id">✓ kayıtlı</template>
                </span>
                <div class="relative">
                  <input
                    v-model="customer.name"
                    type="text"
                    placeholder="isim yaz, kayıtlıysa listeden seç"
                    class="mt-1 w-full rounded-lg border bg-n-background px-4 py-3 text-lg"
                    :class="customer.id ? 'border-green-500' : 'border-n-weak'"
                    @input="onCustomerInput"
                    @focus="custShow = custSuggestions.length > 0"
                    @blur="hideCustSuggestionsSoon"
                  />
                  <span
                    v-if="custSearching"
                    class="absolute right-3 top-1/2 -translate-y-1/2 text-n-slate-10 text-sm animate-pulse"
                  >
                    ⏳
                  </span>
                </div>
                <div
                  v-if="custShow"
                  class="absolute z-30 top-full left-0 right-0 md:min-w-80 bg-n-solid-1 border border-n-weak rounded-lg shadow-lg max-h-72 overflow-auto"
                >
                  <button
                    v-for="c in custSuggestions"
                    :key="c.id"
                    class="w-full text-left px-3 py-3 hover:bg-n-slate-3 border-b border-n-weak last:border-0"
                    @mousedown.prevent="pickCustomer(c)"
                  >
                    <span class="font-bold text-n-slate-12 block">
                      {{ c.name }}
                    </span>
                    <span class="text-sm text-n-slate-11">
                      {{
                        [c.country, c.phone, c.cargo_company]
                          .filter(Boolean)
                          .join(' · ') || '—'
                      }}
                      <template v-if="c.sales_count">
                        · {{ c.sales_count }} fiş
                      </template>
                    </span>
                  </button>
                </div>
              </label>
              <label class="block">
                <span class="text-sm font-semibold text-n-slate-11">Ülke</span>
                <input
                  v-model="customer.country"
                  type="text"
                  placeholder="ör. Arnavutluk"
                  class="mt-1 w-full rounded-lg border border-n-weak bg-n-background px-3 py-3 text-lg"
                />
              </label>
              <label class="block">
                <span class="text-sm font-semibold text-n-slate-11"
                  >Telefon</span
                >
                <input
                  v-model="customer.phone"
                  type="tel"
                  inputmode="tel"
                  class="mt-1 w-full rounded-lg border border-n-weak bg-n-background px-3 py-3 text-lg"
                />
              </label>
              <label class="block">
                <span class="text-sm font-semibold text-n-slate-11">
                  Kargo Firması (özel/ihracat)
                </span>
                <input
                  v-model="customer.cargo_company"
                  type="text"
                  placeholder="müşterinin çalıştığı kargo"
                  class="mt-1 w-full rounded-lg border border-n-weak bg-n-background px-3 py-3 text-lg"
                />
              </label>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mb-4">
              <label class="block">
                <span class="text-sm font-semibold text-n-slate-11"
                  >Satışı Yapan</span
                >
                <select
                  v-model="sellerName"
                  class="mt-1 w-full rounded-lg border border-n-weak bg-n-background px-3 py-3 text-lg"
                >
                  <option v-for="s in SELLERS" :key="s" :value="s">
                    {{ s }}
                  </option>
                </select>
              </label>
              <label class="block">
                <span class="text-sm font-semibold text-n-slate-11">
                  Satış Tarihi (geçmişe kayıt için değiştir)
                </span>
                <input
                  v-model="soldOn"
                  type="date"
                  :max="trToday()"
                  class="mt-1 w-full rounded-lg border bg-n-background px-3 py-3 text-lg"
                  :class="
                    soldOn !== trToday()
                      ? 'border-yellow-500 bg-yellow-50'
                      : 'border-n-weak'
                  "
                />
              </label>
              <label class="block">
                <span class="text-sm font-semibold text-n-slate-11"
                  >Teslim Tarihi</span
                >
                <input
                  v-model="deliveryDate"
                  type="date"
                  class="mt-1 w-full rounded-lg border border-n-weak bg-n-background px-3 py-3 text-lg"
                />
              </label>
            </div>

            <div class="flex flex-wrap gap-4 md:gap-6">
              <div>
                <span class="text-sm font-semibold text-n-slate-11 block mb-1"
                  >Satış Kaynağı</span
                >
                <div class="flex flex-wrap gap-2">
                  <button
                    v-for="(label, key) in sourceLabels"
                    :key="key"
                    class="px-3 py-2.5 rounded-lg text-sm md:text-base font-semibold border-2"
                    :class="
                      source === key
                        ? 'border-woot-600 bg-woot-50 text-woot-700'
                        : 'border-n-weak text-n-slate-11'
                    "
                    @click="source = key"
                  >
                    {{ key === 'fabrika' ? '🏭' : '📦' }} {{ label }}
                  </button>
                </div>
              </div>
              <div>
                <span class="text-sm font-semibold text-n-slate-11 block mb-1"
                  >Para Birimi</span
                >
                <div class="flex gap-2">
                  <button
                    v-for="(s, cur) in symbols"
                    :key="cur"
                    class="px-3 py-2.5 rounded-lg text-sm md:text-base font-bold border-2"
                    :class="
                      currency === cur
                        ? 'border-woot-600 bg-woot-50 text-woot-700'
                        : 'border-n-weak text-n-slate-11'
                    "
                    @click="currency = cur"
                  >
                    {{ s }} {{ cur }}
                  </button>
                </div>
              </div>
              <div>
                <span class="text-sm font-semibold text-n-slate-11 block mb-1"
                  >Fatura</span
                >
                <div class="flex flex-wrap gap-2">
                  <button
                    v-for="(label, key) in invoiceLabels"
                    :key="key"
                    class="px-3 py-2.5 rounded-lg text-sm md:text-base font-semibold border-2"
                    :class="
                      invoiceType === key
                        ? key === 'yarim'
                          ? 'border-yellow-600 bg-yellow-50 text-yellow-800'
                          : 'border-woot-600 bg-woot-50 text-woot-700'
                        : 'border-n-weak text-n-slate-11'
                    "
                    @click="invoiceType = key"
                  >
                    {{
                      key === 'yok'
                        ? 'Fatura Yok'
                        : key === 'tam'
                          ? 'Tam Fatura'
                          : '½ Yarım Fatura'
                    }}
                  </button>
                </div>
              </div>
              <div>
                <span class="text-sm font-semibold text-n-slate-11 block mb-1"
                  >Ödeme Şekli</span
                >
                <div class="flex gap-2">
                  <button
                    v-for="(opt, key) in paymentOptions"
                    :key="key"
                    class="px-3 py-2 rounded-lg border-2 text-left leading-tight"
                    :class="
                      paymentMethod === key
                        ? key === 'havale'
                          ? 'border-woot-600 bg-woot-50 text-woot-700'
                          : 'border-green-600 bg-green-50 text-green-800'
                        : 'border-n-weak text-n-slate-11'
                    "
                    @click="paymentMethod = key"
                  >
                    <span class="block text-sm md:text-base font-bold">
                      {{ opt.icon }} {{ opt.label }}
                    </span>
                    <span class="block text-xs opacity-70">{{ opt.hint }}</span>
                  </button>
                </div>
              </div>
            </div>
            <p
              v-if="invoiceType === 'yarim'"
              class="mt-3 text-sm md:text-base bg-yellow-50 text-yellow-800 rounded-lg px-4 py-2.5"
            >
              ½ Yarım fatura: fişte birim fiyatlar <strong>yarıya iner</strong>,
              KDV yarı tutardan hesaplanır, adetler aynen kalır. Gerçek satış
              tutarı ({{ fmt(realSubtotal) }} {{ sym }}) sistemde saklanır.
            </p>
          </div>

          <!-- ÜRÜN GRUPLARI -->
          <div
            v-for="(group, gi) in groups"
            :key="gi"
            class="bg-n-solid-1 rounded-xl border border-n-weak p-3 md:p-5 mb-4"
            :class="belowCost(group) ? 'border-red-400' : ''"
          >
            <!-- Grup başlığı: kod + fiyat + maliyet -->
            <div
              class="grid grid-cols-2 md:grid-cols-[1.6fr_130px_110px_1fr_40px] gap-2 md:gap-3 items-end"
            >
              <div class="relative col-span-2 md:col-span-1">
                <span class="text-xs font-semibold text-n-slate-11"
                  >Ürün Kodu</span
                >
                <div class="relative">
                  <input
                    v-model="group.code"
                    type="text"
                    inputmode="numeric"
                    class="mt-0.5 w-full rounded-lg border border-n-weak bg-n-background px-3 py-3 text-lg font-bold"
                    @input="onCodeInput(group)"
                    @focus="
                      group.showSuggestions = group.suggestions.length > 0
                    "
                    @blur="hideSuggestionsSoon(group)"
                  />
                  <span
                    v-if="group.searching"
                    class="absolute right-3 top-1/2 -translate-y-1/2 text-n-slate-10 text-sm animate-pulse"
                  >
                    ⏳
                  </span>
                </div>
                <div
                  v-if="group.showSuggestions"
                  class="absolute z-20 top-full left-0 right-0 md:min-w-96 bg-n-solid-1 border border-n-weak rounded-lg shadow-lg max-h-72 overflow-auto"
                >
                  <button
                    v-for="p in group.suggestions"
                    :key="p.parent_code"
                    class="w-full text-left px-3 py-3 hover:bg-n-slate-3 border-b border-n-weak last:border-0"
                    @mousedown.prevent="pickSuggestion(group, p)"
                  >
                    <div class="flex items-center gap-2">
                      <img
                        v-if="p.image"
                        :src="p.image"
                        class="w-9 h-12 object-cover rounded shrink-0"
                      />
                      <div class="min-w-0">
                        <span class="font-bold text-n-slate-12">{{
                          p.model_code
                        }}</span>
                        <span
                          v-if="p.wholesale_usd"
                          class="text-sm text-green-700 font-semibold"
                        >
                          · toptan {{ p.wholesale_usd }}$
                        </span>
                        <p class="text-sm text-n-slate-11 truncate">
                          {{ p.title }}
                        </p>
                      </div>
                    </div>
                  </button>
                </div>
              </div>

              <div>
                <span class="text-xs font-semibold text-n-slate-11">
                  Birim Fiyat ({{ sym }})
                </span>
                <input
                  v-model.number="group.unit_price"
                  type="number"
                  step="0.01"
                  min="0"
                  inputmode="decimal"
                  class="mt-0.5 w-full rounded-lg border px-2 py-3 text-lg text-center font-semibold"
                  :class="
                    belowCost(group)
                      ? 'border-red-500 bg-red-50 text-red-700'
                      : 'border-n-weak bg-n-background'
                  "
                />
              </div>

              <div>
                <span
                  class="text-xs font-semibold"
                  :class="needsCost(group) ? 'text-red-600' : 'text-n-slate-11'"
                >
                  Maliyet ($) *
                </span>
                <input
                  v-model.number="group.cost_usd"
                  type="number"
                  step="0.01"
                  min="0"
                  inputmode="decimal"
                  title="Sadece bu ekranda görünür, fişe girmez"
                  class="mt-0.5 w-full rounded-lg border px-2 py-3 text-lg text-center font-semibold"
                  :class="
                    needsCost(group)
                      ? 'border-red-500 bg-red-50 text-red-700'
                      : 'border-n-weak bg-n-slate-2 text-n-slate-11'
                  "
                />
              </div>

              <div class="min-w-0">
                <p
                  v-if="group.title"
                  class="text-sm text-green-700 font-medium truncate"
                >
                  ✓ {{ group.title }}
                </p>
                <p v-else-if="group.notFound" class="text-sm text-yellow-700">
                  ⚠ Katalogda bulunamadı — rengi elle ekleyip devam
                  edebilirsin.
                </p>
              </div>

              <button
                class="text-n-slate-10 hover:text-red-600 text-xl pb-2 justify-self-end"
                title="Ürünü kaldır"
                @click="removeGroup(group)"
              >
                ✕
              </button>
            </div>

            <p v-if="belowCost(group)" class="text-sm text-red-600 mt-2">
              ⚠ Birim fiyat maliyetin ({{ group.cost_usd }}$) altında!
            </p>
            <p
              v-if="needsCost(group)"
              class="text-sm text-red-600 mt-2 font-semibold"
            >
              ⚠ Maliyet girilmeden satış kaydedilemez — ürün katalogda yoksa
              maliyeti dolar olarak elle gir.
            </p>

            <!-- Renk çipleri: çoklu seçim -->
            <div v-if="group.colors.length" class="mt-3">
              <span class="text-xs font-semibold text-n-slate-11 block mb-1.5">
                Renkler — sattıklarına tıkla
              </span>
              <div class="flex flex-wrap gap-2">
                <button
                  v-for="c in group.colors"
                  :key="c"
                  class="px-3.5 py-2 rounded-full text-base font-semibold border-2"
                  :class="
                    colorSelected(group, c)
                      ? 'border-woot-600 bg-woot-600 text-white'
                      : 'border-n-weak text-n-slate-11 hover:border-woot-400'
                  "
                  @click="toggleColor(group, c)"
                >
                  {{ colorSelected(group, c) ? '✓ ' : '' }}{{ c }}
                </button>
                <button
                  class="px-3.5 py-2 rounded-full text-base font-semibold border-2 border-dashed border-n-weak text-n-slate-10 hover:border-woot-400"
                  @click="addManualRow(group)"
                >
                  ✏️ Başka renk
                </button>
              </div>
            </div>

            <!-- Renk satırları: adetler -->
            <div v-if="group.rows.length" class="mt-3 space-y-2">
              <div
                v-for="(row, ri) in group.rows"
                :key="ri"
                class="grid grid-cols-[1fr_90px_110px_36px] gap-2 items-center bg-n-slate-2 rounded-lg px-3 py-2"
              >
                <input
                  v-if="row.manual"
                  v-model="row.color"
                  type="text"
                  placeholder="renk adı"
                  class="rounded-lg border border-n-weak bg-n-background px-3 py-2.5 text-base"
                />
                <span v-else class="text-lg font-semibold text-n-slate-12">
                  {{ row.color }}
                </span>
                <input
                  v-model.number="row.qty"
                  type="number"
                  min="1"
                  inputmode="numeric"
                  placeholder="adet"
                  class="rounded-lg border border-n-weak bg-n-background px-2 py-2.5 text-lg text-center"
                />
                <span
                  class="text-right font-bold text-n-slate-12 whitespace-nowrap"
                >
                  {{
                    row.qty && group.unit_price
                      ? `${fmt(row.qty * group.unit_price)} ${sym}`
                      : '—'
                  }}
                </span>
                <button
                  class="text-n-slate-10 hover:text-red-600 text-lg text-center"
                  @click="removeRow(group, row)"
                >
                  ✕
                </button>
              </div>
              <p class="text-right text-base text-n-slate-11">
                Ürün toplamı:
                <strong class="text-n-slate-12"
                  >{{ groupQty(group) }} adet · {{ fmt(groupTotal(group)) }}
                  {{ sym }}</strong
                >
              </p>
            </div>

            <!-- Katalog dışı ürün: renk çipi yoksa elle satır -->
            <div v-if="!group.colors.length && group.code" class="mt-3">
              <button
                class="px-4 py-2.5 rounded-lg border-2 border-dashed border-n-weak text-n-slate-11 font-semibold hover:border-woot-600 hover:text-woot-700 text-base"
                @click="addManualRow(group)"
              >
                + Renk / Adet Ekle
              </button>
            </div>
          </div>

          <button
            class="w-full md:w-auto px-5 py-3.5 rounded-xl border-2 border-dashed border-n-weak text-n-slate-11 font-bold hover:border-woot-600 hover:text-woot-700 text-lg mb-4"
            @click="addGroup"
          >
            + Ürün Ekle
          </button>

          <!-- Özet + oluştur -->
          <div class="bg-n-solid-1 rounded-xl border border-n-weak p-4 md:p-5">
            <div class="grid grid-cols-2 md:flex md:gap-8 gap-3 text-lg mb-4">
              <div>
                <span class="text-sm text-n-slate-11 block">Toplam Adet</span>
                <strong class="text-2xl">{{ totalQty }}</strong>
              </div>
              <div>
                <span class="text-sm text-n-slate-11 block">Ara Toplam</span>
                <strong class="text-2xl">{{ fmt(fisGross) }} {{ sym }}</strong>
                <span
                  v-if="invoiceType === 'yarim'"
                  class="text-sm text-yellow-700 block"
                >
                  gerçek: {{ fmt(grossSubtotal) }} {{ sym }}
                </span>
              </div>
              <div>
                <div class="flex gap-1 mb-1">
                  <button
                    class="px-2.5 py-1 rounded-md text-sm font-bold border-2"
                    :class="
                      adjMode === 'iskonto'
                        ? 'border-red-500 bg-red-50 text-red-700'
                        : 'border-n-weak text-n-slate-11'
                    "
                    @click="adjMode = 'iskonto'"
                  >
                    − İskonto
                  </button>
                  <button
                    class="px-2.5 py-1 rounded-md text-sm font-bold border-2"
                    :class="
                      adjMode === 'ilave'
                        ? 'border-green-600 bg-green-50 text-green-800'
                        : 'border-n-weak text-n-slate-11'
                    "
                    @click="adjMode = 'ilave'"
                  >
                    + İlave
                  </button>
                </div>
                <input
                  v-model.number="discountAmount"
                  type="number"
                  step="0.01"
                  min="0"
                  inputmode="decimal"
                  placeholder="0"
                  class="w-28 rounded-lg border px-2 py-2 text-xl text-center font-bold"
                  :class="
                    discountVal > 0
                      ? 'border-red-400 bg-red-50 text-red-700'
                      : discountVal < 0
                        ? 'border-green-500 bg-green-50 text-green-800'
                        : 'border-n-weak bg-n-background'
                  "
                />
                <span
                  v-if="fisDiscount > 0"
                  class="text-sm text-red-600 font-semibold block"
                >
                  − {{ fmt(fisDiscount) }} {{ sym }} düşülür
                </span>
                <span
                  v-else-if="fisDiscount < 0"
                  class="text-sm text-green-700 font-semibold block"
                >
                  + {{ fmt(-fisDiscount) }} {{ sym }} eklenir
                </span>
              </div>
              <div v-if="invoiceType !== 'yok'">
                <span class="text-sm text-n-slate-11 block">KDV %10</span>
                <strong class="text-2xl">{{ fmt(fisKdv) }} {{ sym }}</strong>
              </div>
              <!-- Kargo: normalde gizli; "+ Kargo Ekle" ile açılır -->
              <div>
                <span class="text-sm text-n-slate-11 block mb-1">Kargo</span>
                <button
                  v-if="!shippingOpen"
                  class="px-3 py-2 rounded-lg border-2 border-dashed border-n-weak text-n-slate-11 font-semibold text-base hover:border-woot-600 hover:text-woot-700"
                  @click="shippingOpen = true"
                >
                  + Kargo Ekle
                </button>
                <div v-else class="flex items-center gap-1">
                  <input
                    v-model.number="shippingAmount"
                    type="number"
                    step="0.01"
                    min="0"
                    inputmode="decimal"
                    placeholder="0"
                    class="w-28 rounded-lg border px-2 py-2 text-xl text-center font-bold"
                    :class="
                      shippingVal > 0
                        ? 'border-woot-500 bg-woot-50 text-woot-700'
                        : 'border-n-weak bg-n-background'
                    "
                  />
                  <button
                    class="px-2 py-2 text-n-slate-11 text-xl font-bold"
                    title="Kargoyu kaldır"
                    @click="
                      shippingAmount = null;
                      shippingOpen = false;
                    "
                  >
                    ×
                  </button>
                </div>
                <span
                  v-if="shippingVal > 0"
                  class="text-sm text-woot-700 font-semibold block mt-0.5"
                >
                  + {{ fmt(shippingVal) }} {{ sym }} eklenir
                </span>
              </div>
              <div>
                <span class="text-sm text-n-slate-11 block">Fiş Toplamı</span>
                <strong class="text-2xl md:text-3xl text-woot-700"
                  >{{ fmt(fisTotal) }} {{ sym }}</strong
                >
                <span
                  v-if="shippingVal > 0"
                  class="text-sm text-n-slate-11 block"
                >
                  mal {{ fmt(goodsTotal) }} + kargo {{ fmt(shippingVal) }}
                  {{ sym }}
                </span>
              </div>
            </div>
            <div class="flex flex-col md:flex-row gap-3 md:items-end">
              <label class="block flex-1">
                <span class="text-sm font-semibold text-n-slate-11">Not</span>
                <input
                  v-model="note"
                  type="text"
                  class="mt-1 w-full rounded-lg border border-n-weak bg-n-background px-3 py-2.5 text-base"
                />
              </label>
              <button
                :disabled="!canPreview || saving"
                class="w-full md:w-auto px-8 py-4 rounded-xl bg-woot-600 text-white text-xl font-bold disabled:opacity-40"
                @click="previewOpen = true"
              >
                Fişi Önizle →
              </button>
            </div>
          </div>
          <p
            v-if="groupsMissingCost.length"
            class="mt-3 text-red-600 font-semibold text-base"
          >
            ⚠ {{ groupsMissingCost.length }} üründe maliyet eksik — Maliyet ($)
            kutuları doldurulmadan fiş kesilemez.
          </p>
          <p v-if="error" class="mt-3 text-red-600 font-semibold text-lg">
            {{ error }}
          </p>
        </template>
      </div>

      <!-- ============ SATIŞ DEFTERİ ============ -->
      <div v-else-if="tab === 'defter'">
        <div class="flex flex-col md:flex-row gap-2 md:gap-3 mb-4">
          <input
            v-model="defterQ"
            type="text"
            placeholder="Müşteri veya fiş no ara"
            class="flex-1 rounded-lg border border-n-weak bg-n-background px-4 py-3 text-lg"
            @keyup.enter="loadDefter"
          />
          <div class="flex gap-2">
            <select
              v-model="defterSource"
              class="flex-1 rounded-lg border border-n-weak bg-n-background px-3 py-3 text-lg"
              @change="loadDefter"
            >
              <option value="">Tüm Kaynaklar</option>
              <option value="fabrika">🏭 Fabrika</option>
              <option value="internet_depo">📦 İnternet Depo</option>
            </select>
            <button
              class="px-5 py-3 rounded-lg bg-woot-600 text-white font-semibold"
              @click="loadDefter"
            >
              Ara
            </button>
          </div>
        </div>

        <div
          v-if="defterLoading"
          class="text-center py-12 text-n-slate-11 text-lg"
        >
          Yükleniyor…
        </div>
        <div
          v-else-if="!sales.length"
          class="text-center py-12 text-n-slate-11 text-lg"
        >
          Henüz kayıtlı satış yok.
        </div>

        <!-- Mobil: kart listesi -->
        <div v-else class="md:hidden space-y-3">
          <div
            v-for="s in sales"
            :key="s.id"
            class="bg-n-solid-1 rounded-xl border border-n-weak p-4"
            :class="s.status === 'cancelled' ? 'opacity-45' : ''"
          >
            <div class="flex justify-between items-baseline mb-1">
              <strong
                class="text-lg"
                :class="s.status === 'cancelled' ? 'line-through' : ''"
              >
                {{ s.customer_name }}
              </strong>
              <span class="font-bold whitespace-nowrap">
                {{ fmt(s.totals.fis_total) }} {{ s.currency_symbol }}
              </span>
            </div>
            <p class="text-sm text-n-slate-11 mb-2">
              {{ s.fis_no }} · {{ s.created_at }} · {{ s.seller_name }} ·
              {{ s.totals.qty }} adet
            </p>
            <div class="flex flex-wrap items-center gap-2">
              <span
                class="px-2.5 py-1 rounded-full text-sm font-semibold"
                :class="
                  s.source === 'fabrika'
                    ? 'bg-violet-100 text-violet-800'
                    : 'bg-woot-100 text-woot-800'
                "
              >
                {{ s.source === 'fabrika' ? '🏭 Fabrika' : '📦 İnt. Depo' }}
              </span>
              <span
                class="px-2.5 py-1 rounded-full text-sm font-semibold"
                :class="
                  s.invoice_type === 'yarim'
                    ? 'bg-yellow-100 text-yellow-800'
                    : s.invoice_type === 'tam'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-n-slate-3 text-n-slate-11'
                "
              >
                {{
                  s.invoice_type === 'yok'
                    ? 'Fatura Yok'
                    : s.invoice_type === 'tam'
                      ? 'Tam Fatura'
                      : '½ Yarım'
                }}
              </span>
              <span
                v-if="s.payment_method"
                class="px-2.5 py-1 rounded-full text-sm font-semibold"
                :class="
                  s.payment_method === 'havale'
                    ? 'bg-woot-100 text-woot-800'
                    : 'bg-green-100 text-green-800'
                "
              >
                {{ s.payment_method === 'havale' ? '🏦 Havale' : '💵 Elden' }}
              </span>
              <span
                v-if="s.totals.shipping > 0"
                class="px-2.5 py-1 rounded-full text-sm font-semibold bg-n-slate-3 text-n-slate-12"
              >
                🚚 Kargo {{ fmt(s.totals.shipping) }} {{ s.currency_symbol }}
              </span>
              <span class="flex-1" />
              <a
                :href="s.fis_url"
                target="_blank"
                class="text-woot-600 font-semibold"
                >Fiş</a
              >
              <button
                v-if="s.status !== 'cancelled'"
                class="text-n-slate-11 font-semibold"
                @click="resendSale(s)"
              >
                Gönder
              </button>
              <button
                v-if="s.status !== 'cancelled'"
                class="text-woot-600 font-semibold"
                @click="startEdit(s)"
              >
                ✏️ Düzenle
              </button>
              <button
                v-if="s.status !== 'cancelled'"
                class="text-red-600 font-semibold"
                @click="cancelSale(s)"
              >
                İptal
              </button>
              <button class="text-red-700 font-bold" @click="deleteSale(s)">
                🗑 Sil
              </button>
            </div>
          </div>
        </div>

        <!-- Masaüstü: tablo -->
        <div
          v-if="sales.length"
          class="hidden md:block bg-n-solid-1 rounded-xl border border-n-weak overflow-x-auto"
        >
          <table class="w-full text-base">
            <thead>
              <tr
                class="text-left text-sm text-n-slate-11 border-b border-n-weak"
              >
                <th class="px-4 py-3">Tarih</th>
                <th class="px-4 py-3">Fiş No</th>
                <th class="px-4 py-3">Müşteri</th>
                <th class="px-4 py-3">Satıcı</th>
                <th class="px-4 py-3">Kaynak</th>
                <th class="px-4 py-3">Fatura</th>
                <th class="px-4 py-3">Ödeme</th>
                <th class="px-4 py-3 text-right">Adet</th>
                <th class="px-4 py-3 text-right">Tutar</th>
                <th class="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="s in sales"
                :key="s.id"
                class="border-b border-n-weak last:border-0"
                :class="
                  s.status === 'cancelled' ? 'opacity-45 line-through' : ''
                "
              >
                <td class="px-4 py-3 whitespace-nowrap">{{ s.created_at }}</td>
                <td class="px-4 py-3 font-bold">{{ s.fis_no }}</td>
                <td class="px-4 py-3 font-semibold">{{ s.customer_name }}</td>
                <td class="px-4 py-3">{{ s.seller_name }}</td>
                <td class="px-4 py-3">
                  <span
                    class="px-2.5 py-1 rounded-full text-sm font-semibold"
                    :class="
                      s.source === 'fabrika'
                        ? 'bg-violet-100 text-violet-800'
                        : 'bg-woot-100 text-woot-800'
                    "
                  >
                    {{ s.source === 'fabrika' ? '🏭 Fabrika' : '📦 İnt. Depo' }}
                  </span>
                </td>
                <td class="px-4 py-3">
                  <span
                    class="px-2.5 py-1 rounded-full text-sm font-semibold"
                    :class="
                      s.invoice_type === 'yarim'
                        ? 'bg-yellow-100 text-yellow-800'
                        : s.invoice_type === 'tam'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-n-slate-3 text-n-slate-11'
                    "
                  >
                    {{
                      s.invoice_type === 'yok'
                        ? 'Yok'
                        : s.invoice_type === 'tam'
                          ? 'Tam'
                          : '½ Yarım'
                    }}
                  </span>
                </td>
                <td class="px-4 py-3">
                  <span
                    v-if="s.payment_method"
                    class="px-2.5 py-1 rounded-full text-sm font-semibold whitespace-nowrap"
                    :class="
                      s.payment_method === 'havale'
                        ? 'bg-woot-100 text-woot-800'
                        : 'bg-green-100 text-green-800'
                    "
                  >
                    {{
                      s.payment_method === 'havale' ? '🏦 Havale' : '💵 Elden'
                    }}
                  </span>
                  <span v-else class="text-n-slate-10">—</span>
                </td>
                <td class="px-4 py-3 text-right">{{ s.totals.qty }}</td>
                <td class="px-4 py-3 text-right font-bold whitespace-nowrap">
                  {{ fmt(s.totals.fis_total) }} {{ s.currency_symbol }}
                  <span
                    v-if="s.totals.shipping > 0"
                    class="block text-sm font-semibold text-n-slate-11"
                  >
                    🚚 kargo {{ fmt(s.totals.shipping) }}
                  </span>
                </td>
                <td class="px-4 py-3 whitespace-nowrap">
                  <a
                    :href="s.fis_url"
                    target="_blank"
                    class="text-woot-600 font-semibold mr-3"
                  >
                    Fiş
                  </a>
                  <button
                    v-if="s.status !== 'cancelled'"
                    class="text-n-slate-11 font-semibold mr-3"
                    @click="resendSale(s)"
                  >
                    Gönder
                  </button>
                  <button
                    v-if="s.status !== 'cancelled'"
                    class="text-woot-600 font-semibold mr-3"
                    @click="startEdit(s)"
                  >
                    Düzenle
                  </button>
                  <button
                    v-if="s.status !== 'cancelled'"
                    class="text-red-600 font-semibold mr-3"
                    @click="cancelSale(s)"
                  >
                    İptal
                  </button>
                  <button class="text-red-700 font-bold" @click="deleteSale(s)">
                    Sil
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ============ 👤 MÜŞTERİLER (iç müşteri kartları) ============ -->
      <div v-else-if="tab === 'musteriler'">
        <div class="flex flex-col md:flex-row gap-2 md:gap-3 mb-4">
          <input
            v-model="customersQ"
            type="text"
            placeholder="İsim, ülke, telefon veya kargo firması ara"
            class="flex-1 rounded-lg border border-n-weak bg-n-background px-4 py-3 text-lg"
            @keyup.enter="loadCustomers"
          />
          <button
            class="px-6 py-3 rounded-lg bg-woot-600 text-white font-semibold"
            @click="loadCustomers"
          >
            Ara
          </button>
        </div>

        <div
          v-if="customersLoading"
          class="text-center py-12 text-n-slate-11 text-lg"
        >
          Yükleniyor…
        </div>
        <div
          v-else-if="!customersList.length"
          class="text-center py-12 text-n-slate-11 text-lg"
        >
          Kayıtlı müşteri yok — Yeni Satış'ta müşteri girildikçe kartlar
          kendiliğinden oluşur.
        </div>

        <div v-else class="space-y-3">
          <div
            v-for="c in customersList"
            :key="c.id"
            class="bg-n-solid-1 rounded-xl border border-n-weak p-4"
          >
            <div
              class="flex flex-wrap justify-between items-baseline gap-2 mb-3"
            >
              <strong class="text-xl text-n-slate-12">{{ c.name }}</strong>
              <span class="text-base text-n-slate-11">
                {{ c.sales_count }} fiş
                <template v-if="c.revenue_try">
                  · {{ fmt(c.revenue_try) }} ₺
                </template>
                <template v-if="c.last_sale">
                  · son satış {{ c.last_sale }}
                </template>
              </span>
            </div>
            <div
              class="grid grid-cols-1 md:grid-cols-[1.6fr_1fr_1.2fr_1.6fr_auto] gap-2 md:gap-3 items-end"
            >
              <label class="block">
                <span class="text-xs font-semibold text-n-slate-11">İsim</span>
                <input
                  v-model="c.name"
                  type="text"
                  class="mt-0.5 w-full rounded-lg border border-n-weak bg-n-background px-3 py-2.5 text-base font-semibold"
                />
              </label>
              <label class="block">
                <span class="text-xs font-semibold text-n-slate-11">Ülke</span>
                <input
                  v-model="c.country"
                  type="text"
                  class="mt-0.5 w-full rounded-lg border border-n-weak bg-n-background px-3 py-2.5 text-base"
                />
              </label>
              <label class="block">
                <span class="text-xs font-semibold text-n-slate-11"
                  >Telefon</span
                >
                <input
                  v-model="c.phone"
                  type="tel"
                  class="mt-0.5 w-full rounded-lg border border-n-weak bg-n-background px-3 py-2.5 text-base"
                />
              </label>
              <label class="block">
                <span class="text-xs font-semibold text-n-slate-11">
                  Kargo Firması (özel/ihracat)
                </span>
                <input
                  v-model="c.cargo_company"
                  type="text"
                  class="mt-0.5 w-full rounded-lg border border-n-weak bg-n-background px-3 py-2.5 text-base"
                />
              </label>
              <button
                :disabled="savingCustomerId === c.id"
                class="px-5 py-2.5 rounded-lg font-semibold disabled:opacity-50"
                :class="
                  c.saved
                    ? 'bg-green-100 text-green-800'
                    : 'bg-woot-600 text-white'
                "
                @click="saveCustomer(c)"
              >
                {{ c.saved ? '✓ Kaydedildi' : 'Kaydet' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- ============ 📊 GÜNLÜK RAPOR (satış · maliyet · kâr) ============ -->
      <div v-else>
        <!-- Tarih kontrolleri: tarih seçilir, "Raporu Getir" ile çalıştırılır -->
        <div
          class="bg-n-solid-1 rounded-xl border border-n-weak p-3 md:p-4 mb-4"
        >
          <div class="flex flex-wrap items-center gap-2 mb-3">
            <button
              class="px-4 py-3 rounded-lg bg-n-slate-3 text-n-slate-12 text-xl font-bold"
              title="Önceki gün"
              @click="shiftDay(-1)"
            >
              ‹
            </button>
            <input
              v-model="repFrom"
              type="date"
              class="rounded-lg border border-n-weak bg-n-background px-3 py-3 text-lg"
            />
            <span class="text-n-slate-10">→</span>
            <input
              v-model="repTo"
              type="date"
              class="rounded-lg border border-n-weak bg-n-background px-3 py-3 text-lg"
            />
            <button
              class="px-4 py-3 rounded-lg bg-n-slate-3 text-n-slate-12 text-xl font-bold"
              title="Sonraki gün"
              @click="shiftDay(1)"
            >
              ›
            </button>
            <button
              :disabled="reportLoading"
              class="px-6 py-3 rounded-lg bg-woot-600 text-white text-lg font-bold disabled:opacity-50"
              @click="loadReport"
            >
              {{ reportLoading ? 'Hazırlanıyor…' : '📊 Raporu Getir' }}
            </button>
          </div>
          <div class="flex flex-wrap items-center gap-2">
            <button
              class="px-4 py-2.5 rounded-lg bg-n-slate-3 text-n-slate-11 font-semibold"
              @click="setToday"
            >
              Bugün
            </button>
            <button
              class="px-4 py-2.5 rounded-lg bg-n-slate-3 text-n-slate-11 font-semibold"
              @click="setLast7"
            >
              Son 7 Gün
            </button>
            <button
              class="px-4 py-2.5 rounded-lg bg-n-slate-3 text-n-slate-11 font-semibold"
              @click="setMonth"
            >
              Bu Ay
            </button>
            <span class="flex-1" />
            <button
              :disabled="pdfLoading || reportLoading"
              class="px-5 py-2.5 rounded-lg bg-red-600 text-white font-bold disabled:opacity-50"
              @click="downloadPdf"
            >
              {{ pdfLoading ? 'PDF hazırlanıyor…' : '⬇️ PDF İndir' }}
            </button>
          </div>
        </div>

        <div
          v-if="reportLoading"
          class="text-center py-12 text-n-slate-11 text-lg"
        >
          Yükleniyor…
        </div>

        <template v-else-if="report">
          <!-- Sade Türkçe özet + sesli okuma -->
          <div
            class="bg-n-solid-1 rounded-xl border border-n-weak p-4 md:p-5 mb-4"
          >
            <div class="flex items-start justify-between gap-3">
              <p
                class="text-xl md:text-2xl leading-relaxed text-n-slate-12 font-medium"
              >
                {{ report.ozet }}
              </p>
              <button
                class="shrink-0 px-4 py-3 rounded-lg bg-violet-100 text-violet-800 text-lg font-semibold"
                title="Özeti sesli oku"
                @click="speakOzet"
              >
                🔊 Oku
              </button>
            </div>
          </div>

          <!-- Rakam kartları -->
          <div class="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            <div class="bg-n-solid-1 rounded-xl border border-n-weak p-4">
              <span class="text-sm text-n-slate-11 block">Satış Fişi</span>
              <strong class="text-3xl text-n-slate-12">{{
                report.summary.sales_count
              }}</strong>
            </div>
            <div class="bg-n-solid-1 rounded-xl border border-n-weak p-4">
              <span class="text-sm text-n-slate-11 block">Toplam Adet</span>
              <strong class="text-3xl text-n-slate-12">{{
                report.summary.qty
              }}</strong>
            </div>
            <div class="bg-n-solid-1 rounded-xl border border-n-weak p-4">
              <span class="text-sm text-n-slate-11 block">Ciro</span>
              <strong class="text-3xl text-n-slate-12"
                >{{ fmt(report.summary.revenue_try) }} ₺</strong
              >
              <span
                v-for="c in report.summary.by_currency"
                :key="c.currency"
                class="block text-sm text-n-slate-11"
              >
                {{ c.count }} fiş · {{ fmt(c.revenue) }} {{ c.symbol }}
              </span>
            </div>
            <div class="bg-n-solid-1 rounded-xl border border-n-weak p-4">
              <span class="text-sm text-n-slate-11 block">Maliyet</span>
              <strong class="text-3xl text-n-slate-12"
                >{{ fmt(report.summary.cost_try) }} ₺</strong
              >
            </div>
            <div
              class="rounded-xl border-2 p-4 col-span-2 md:col-span-1"
              :class="
                report.summary.profit_try >= 0
                  ? 'bg-green-50 border-green-500'
                  : 'bg-red-50 border-red-500'
              "
            >
              <span
                class="text-sm font-semibold block"
                :class="
                  report.summary.profit_try >= 0
                    ? 'text-green-800'
                    : 'text-red-700'
                "
              >
                {{ report.summary.profit_try >= 0 ? 'KÂR' : 'ZARAR' }}
              </span>
              <strong
                class="text-3xl md:text-4xl"
                :class="
                  report.summary.profit_try >= 0
                    ? 'text-green-800'
                    : 'text-red-700'
                "
              >
                {{ fmt(Math.abs(report.summary.profit_try)) }} ₺
              </strong>
            </div>
          </div>

          <p
            v-if="report.summary.kur_usd_try"
            class="text-sm text-n-slate-11 mb-4"
          >
            Kur: 1 $ = {{ fmt(report.summary.kur_usd_try) }} ₺ (TCMB). Kâr, KDV
            hariç gerçek satış tutarından maliyet düşülerek TL hesaplanır; her
            fişte satış anındaki kur kullanılır.
          </p>

          <!-- Uyarılar -->
          <p
            v-if="report.summary.incomplete_sales"
            class="mb-4 bg-yellow-50 text-yellow-800 rounded-lg px-4 py-3 text-base font-semibold"
          >
            ⚠ {{ report.summary.incomplete_sales }} fişin maliyeti eksik — kârı
            hesaba katılamadı.
          </p>

          <!-- Satıcı kırılımı -->
          <div
            v-if="report.sellers.length > 1"
            class="bg-n-solid-1 rounded-xl border border-n-weak p-4 md:p-5 mb-4"
          >
            <h3 class="text-lg font-bold text-n-slate-12 mb-3">
              Satıcı Kırılımı
            </h3>
            <div
              v-for="b in report.sellers"
              :key="b.name"
              class="flex flex-wrap justify-between gap-2 py-2.5 border-b border-n-weak last:border-0 text-lg"
            >
              <strong class="text-n-slate-12">{{ b.name }}</strong>
              <span class="text-n-slate-11">
                {{ b.count }} fiş · {{ b.qty }} adet ·
                <strong class="text-n-slate-12"
                  >{{ fmt(b.revenue_try) }} ₺</strong
                >
                · kâr
                <strong
                  :class="b.profit_try >= 0 ? 'text-green-700' : 'text-red-600'"
                >
                  {{ fmt(b.profit_try) }} ₺
                </strong>
              </span>
            </div>
          </div>

          <!-- Fiş listesi -->
          <div
            v-if="!report.sales.length"
            class="text-center py-12 text-n-slate-11 text-lg"
          >
            Bu dönemde satış yok.
          </div>
          <div v-else class="space-y-3">
            <div
              v-for="r in report.sales"
              :key="r.id"
              class="bg-n-solid-1 rounded-xl border border-n-weak p-4"
            >
              <div class="flex flex-wrap justify-between items-start gap-2">
                <div class="min-w-0">
                  <strong class="text-xl text-n-slate-12 block truncate">
                    {{ r.customer }}
                  </strong>
                  <p class="text-sm text-n-slate-11 mt-0.5">
                    {{ r.fis_no }} · {{ r.time }} · Satıcı: {{ r.seller }}
                  </p>
                </div>
                <span
                  class="px-3 py-1.5 rounded-full text-base font-bold whitespace-nowrap"
                  :class="
                    r.cost_missing
                      ? 'bg-yellow-100 text-yellow-800'
                      : r.profit_try >= 0
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-700'
                  "
                >
                  {{
                    r.cost_missing
                      ? '⚠ Maliyet eksik'
                      : r.profit_try >= 0
                        ? `KÂR ${fmt(r.profit_try)} ₺`
                        : `ZARAR ${fmt(-r.profit_try)} ₺`
                  }}
                </span>
              </div>
              <div
                class="flex flex-wrap gap-x-8 gap-y-2 mt-3 pt-3 border-t border-n-weak"
              >
                <div>
                  <span class="text-sm text-n-slate-11 block">Adet</span>
                  <strong class="text-xl text-n-slate-12">{{ r.qty }}</strong>
                </div>
                <div>
                  <span class="text-sm text-n-slate-11 block">Tutar</span>
                  <strong class="text-xl text-n-slate-12">
                    {{ fmt(r.revenue) }} {{ r.currency_symbol }}
                  </strong>
                </div>
                <div v-if="r.currency !== 'TRY' && r.revenue_try">
                  <span class="text-sm text-n-slate-11 block"
                    >TL Karşılığı</span
                  >
                  <strong class="text-xl text-n-slate-12">
                    ≈ {{ fmt(r.revenue_try) }} ₺
                  </strong>
                </div>
                <div v-if="r.discount > 0">
                  <span class="text-sm text-n-slate-11 block">İskonto</span>
                  <strong class="text-xl text-red-600">
                    − {{ fmt(r.discount) }} {{ r.currency_symbol }}
                  </strong>
                </div>
                <div v-else-if="r.discount < 0">
                  <span class="text-sm text-n-slate-11 block">İlave</span>
                  <strong class="text-xl text-green-700">
                    + {{ fmt(-r.discount) }} {{ r.currency_symbol }}
                  </strong>
                </div>
                <div v-if="r.shipping > 0">
                  <span class="text-sm text-n-slate-11 block">🚚 Kargo</span>
                  <strong class="text-xl text-n-slate-12">
                    + {{ fmt(r.shipping) }} {{ r.currency_symbol }}
                  </strong>
                  <span class="text-xs text-n-slate-11 block">kâra girmez</span>
                </div>
                <div v-if="r.payment_method">
                  <span class="text-sm text-n-slate-11 block">Ödeme</span>
                  <strong
                    class="text-xl"
                    :class="
                      r.payment_method === 'havale'
                        ? 'text-woot-700'
                        : 'text-green-700'
                    "
                  >
                    {{
                      r.payment_method === 'havale' ? '🏦 Havale' : '💵 Elden'
                    }}
                  </strong>
                </div>
              </div>
            </div>
          </div>

          <p
            v-if="report.summary.cancelled_count"
            class="mt-4 text-sm text-n-slate-11"
          >
            {{ report.summary.cancelled_count }} iptal fiş bu dönemde —
            rakamlara dahil edilmedi.
          </p>
        </template>
      </div>
    </div>

    <!-- ============ ÖNİZLEME ============ -->
    <div
      v-if="previewOpen"
      class="fixed inset-0 z-50 bg-black/50 flex items-end md:items-center justify-center md:p-4"
      @click.self="previewOpen = false"
    >
      <div
        class="bg-white rounded-t-2xl md:rounded-2xl max-w-xl w-full max-h-[92vh] overflow-auto p-5 md:p-7"
      >
        <div
          class="flex justify-between items-baseline border-b-4 border-slate-900 pb-3 mb-4"
        >
          <h2
            class="text-lg md:text-xl font-extrabold tracking-wide text-slate-900"
          >
            ADROM · SİPARİŞ FİŞİ
          </h2>
          <span class="text-slate-500 font-semibold text-sm">önizleme</span>
        </div>
        <div class="grid grid-cols-2 gap-2 mb-4 text-slate-800">
          <div>
            <span class="text-sm text-slate-500 block">Müşteri</span
            ><strong>{{ customer.name }}</strong>
          </div>
          <div>
            <span class="text-sm text-slate-500 block">Satışı Yapan</span
            >{{ sellerName }}
          </div>
          <div>
            <span class="text-sm text-slate-500 block">Satış Türü</span
            >{{ sourceLabels[source] }}
          </div>
          <div v-if="deliveryDate">
            <span class="text-sm text-slate-500 block">Teslim</span
            >{{ deliveryDate }}
          </div>
          <div>
            <span class="text-sm text-slate-500 block">Ödeme Şekli</span>
            <strong>
              {{ paymentOptions[paymentMethod].icon }}
              {{
                paymentMethod === 'havale' ? 'Havale / EFT' : 'Elden (nakit)'
              }}
            </strong>
          </div>
        </div>
        <table class="w-full mb-4 text-slate-900">
          <thead>
            <tr
              class="text-sm text-slate-500 border-b-2 border-slate-200 text-left"
            >
              <th class="py-2">Model</th>
              <th>Renk</th>
              <th class="text-right">Adet</th>
              <th class="text-right">Birim</th>
              <th class="text-right">Tutar</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(l, i) in validLines"
              :key="i"
              class="border-b border-slate-100"
            >
              <td class="py-2.5 font-bold">{{ l.code }}</td>
              <td>{{ l.color }}</td>
              <td class="text-right">{{ l.qty }}</td>
              <td class="text-right">
                {{
                  fmt(invoiceType === 'yarim' ? l.unit_price / 2 : l.unit_price)
                }}
                {{ sym }}
              </td>
              <td class="text-right">
                {{
                  fmt(
                    l.qty *
                      (invoiceType === 'yarim'
                        ? l.unit_price / 2
                        : l.unit_price)
                  )
                }}
                {{ sym }}
              </td>
            </tr>
          </tbody>
        </table>
        <div class="text-right space-y-1 text-slate-800 mb-4">
          <div>
            Toplam Adet: <strong>{{ totalQty }}</strong>
          </div>
          <div>Ara Toplam (KDV hariç): {{ fmt(fisGross) }} {{ sym }}</div>
          <div v-if="fisDiscount > 0" class="text-red-600 font-bold">
            İskonto: − {{ fmt(fisDiscount) }} {{ sym }}
          </div>
          <div v-else-if="fisDiscount < 0" class="text-green-700 font-bold">
            İlave: + {{ fmt(-fisDiscount) }} {{ sym }}
          </div>
          <div v-if="fisDiscount !== 0">
            {{ fisDiscount > 0 ? 'İskonto' : 'İlave' }} Sonrası:
            {{ fmt(fisSubtotal) }} {{ sym }}
          </div>
          <div v-if="invoiceType !== 'yok'">
            KDV %10: {{ fmt(fisKdv) }} {{ sym }}
          </div>
          <div v-if="shippingVal > 0" class="font-bold">
            Kargo{{
              customer.cargo_company ? ' · ' + customer.cargo_company : ''
            }}: + {{ fmt(shippingVal) }} {{ sym }}
          </div>
          <div
            class="text-xl md:text-2xl font-extrabold border-t-4 border-slate-900 pt-2"
          >
            GENEL TOPLAM: {{ fmt(fisTotal) }} {{ sym }}
          </div>
        </div>
        <p
          v-if="invoiceType === 'yarim'"
          class="bg-yellow-50 text-yellow-800 font-bold rounded-lg px-4 py-2.5 mb-4"
        >
          ⚠ Yarım fatura talepli
        </p>
        <p v-if="note" class="text-slate-600 mb-4">Not: {{ note }}</p>
        <div class="flex flex-col md:flex-row gap-2 md:gap-3 md:justify-end">
          <button
            :disabled="saving"
            class="order-1 md:order-3 px-6 py-3.5 rounded-lg bg-woot-600 text-white text-lg font-bold disabled:opacity-50"
            @click="submit(false)"
          >
            {{
              saving
                ? 'Kaydediliyor…'
                : editingSale
                  ? '📲 Güncelle ve Gönder'
                  : '📲 Oluştur ve Gönder'
            }}
          </button>
          <button
            :disabled="saving"
            class="order-2 px-5 py-3 rounded-lg bg-slate-200 text-slate-800 font-semibold disabled:opacity-50"
            @click="submit(true)"
          >
            {{ editingSale ? 'Göndermeden Güncelle' : 'Göndermeden Kaydet' }}
          </button>
          <button
            class="order-3 md:order-1 px-5 py-3 rounded-lg bg-slate-100 text-slate-700 font-semibold"
            @click="previewOpen = false"
          >
            ← Düzelt
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
